import { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, BarChart2 } from 'lucide-react';
import { useRouter } from '@/context/RouterContext';
import { useI18n } from '@/context/I18nContext';
import { rpc, RpcError } from '@/api/client';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorState } from '@/components/common/ErrorState';
import { ToolHeader } from '@/components/common/ToolHeader';
import { TheoreticalBest } from './TheoreticalBest';
import { SectorTabs } from './SectorTabs';
import { SectorPanel } from './SectorPanel';
import { LapStatsTable } from './LapStatsTable';
import { SessionSelector } from './SessionSelector';
import { SplitPoints } from './SplitPoints';
import { WizardStepper } from './WizardStepper';
import { SessionRemovedAnalysisDialog } from '@/components/analysis/SessionRemovedAnalysisDialog';
import { useWizardState } from '@/hooks/useWizardState';
import { SectorTrackMap } from './SectorTrackMap';
import type { TrackShape, TrackAnnotation, TrackPoint } from '@/lib/spline';
import { sampleClosedSpline, findClosestParametricT, getPointAtT } from '@/lib/spline';
import type { SessionSummary, ListSessionsResponse, ListAnalysisTracksResponse, SessionDetailResponse, AnalyzeSectorResponse, SectorData, AnalyzeSectorRequest, AnalyzeResponse, GetAnalysisResponse, AnalysisRecord, SectorsSelection } from '@/types/telemetry';

interface SectorAnalysisProps {
  trackName: string;
  initialSessionId?: string;
}

export function SectorAnalysis({ trackName, initialSessionId }: SectorAnalysisProps) {
  const { navigate } = useRouter();
  const { t } = useI18n();
  const wizardSteps = [t('sector.wizardChooseTrack'), t('sector.wizardDefine'), t('sector.wizardSelect'), t('sector.wizardResults')];
  const [wizardState, updateWizardState] = useWizardState({
    step: trackName ? (initialSessionId ? 3 : 1) : 0,
    draftTrack: (trackName || undefined) as string | undefined,
    selectedIds: [] as string[],
    driverFilter: 'all',
    weatherFilter: 'all',
    splitPositions: [] as number[],
    activeSector: 0,
    activeMetric: 'time',
  });

  const { step, draftTrack, selectedIds: selectedIdsArr, driverFilter, weatherFilter, splitPositions, activeSector, activeMetric } = wizardState;
  const selectedIds = new Set(selectedIdsArr);

  const setStep = useCallback((newStep: number | ((prev: number) => number)) => {
    updateWizardState(prev => {
      const nextStep = typeof newStep === 'function' ? newStep(prev.step) : newStep;
      // Push state on step change so browser back button works
      return { step: nextStep };
    }, false); 
  }, [updateWizardState]);

  const setDraftTrack = useCallback((track: string | undefined) => updateWizardState({ draftTrack: track }), [updateWizardState]);
  const setSelectedIds = useCallback((ids: Set<string> | ((prev: Set<string>) => Set<string>)) => {
    updateWizardState(prev => {
      const nextIds = typeof ids === 'function' ? ids(new Set(prev.selectedIds)) : ids;
      return { selectedIds: [...nextIds] };
    });
  }, [updateWizardState]);
  const setDriverFilter = useCallback((filter: string) => updateWizardState({ driverFilter: filter }), [updateWizardState]);
  const setWeatherFilter = useCallback((filter: string) => updateWizardState({ weatherFilter: filter }), [updateWizardState]);
  const setSplitPositions = useCallback((splits: number[] | ((prev: number[]) => number[])) => {
    updateWizardState(prev => {
      const nextSplits = typeof splits === 'function' ? splits(prev.splitPositions) : splits;
      return { splitPositions: nextSplits };
    });
  }, [updateWizardState]);

  const setActiveSector = useCallback((sector: number) => updateWizardState({ activeSector: sector }), [updateWizardState]);
  const setActiveMetric = useCallback((metric: string) => updateWizardState({ activeMetric: metric }), [updateWizardState]);

  const [sessions, setSessions] = useState<SessionSummary[]>([]);
  const [availableTracks, setAvailableTracks] = useState<string[]>([]);
  const [trackId, setTrackId] = useState('');
  const [refSamples, setRefSamples] = useState<any[]>([]);
  const [trackShape, setTrackShape] = useState<TrackShape | null>(null);
  const [trackAnnotations, setTrackAnnotations] = useState<TrackAnnotation[]>([]);
  const [splitPoints, setSplitPoints] = useState<TrackPoint[]>([]);
  
  const [analysisResult, setAnalysisResult] = useState<AnalyzeSectorResponse | null>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState<string | undefined>(draftTrack || trackName || undefined);
  const [loading, setLoading] = useState(!trackName);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [sessionRemovedAnalysis, setSessionRemovedAnalysis] = useState<AnalysisRecord | null>(null);

  const handleLoadAnalysis = useCallback(async (analysis: AnalysisRecord) => {
    const sec = analysis.sector;
    if (sec?.track) {
      const tName = sec.track.trackName || sec.track.trackId;
      setSelectedTrack(tName);
      setTrackId(sec.track.trackId);
    }
    if (sec?.sessions?.sessionIds) {
      setSelectedIds(new Set(sec.sessions.sessionIds));
    }
    if (sec?.sectors?.splitPositions) {
      setSplitPositions(sec.sectors.splitPositions);
    }
    if (sec?.sectors?.splitPoints) {
      setSplitPoints(sec.sectors.splitPoints);
    }
    setAnalyzing(true);
    try {
      if (analysis.id) {
        const resp = await rpc<GetAnalysisResponse>('GetAnalysis', { id: analysis.id });
        if (resp.sector) {
          setAnalysisResult(resp.sector);
          setActiveSector(0);
          setStep(3);
        }
      } else {
        const resp = await rpc<AnalyzeResponse>('Analyze', {
          sector: sec,
          skipSave: true,
        });
        if (resp.sector) {
          setAnalysisResult(resp.sector);
          setActiveSector(0);
          setStep(3);
        }
      }
    } catch (err: any) {
      console.error('Failed to evaluate loaded analysis:', err);
      const isSessionMissing = err?.status === 404 || err?.isNotFound || (err?.message && (err.message.includes('session') || err.message.includes('not found')));
      if (isSessionMissing) {
        setSessionRemovedAnalysis(analysis);
      } else {
        setError(err?.message || 'Failed to evaluate analysis');
      }
    } finally {
      setAnalyzing(false);
    }
  }, [setStep, setSelectedIds, setSplitPositions, setActiveSector]);

  // Handle restoring analysis from history navigation
  useEffect(() => {
    const stored = sessionStorage.getItem('loaded_analysis_record');
    if (stored) {
      sessionStorage.removeItem('loaded_analysis_record');
      try {
        const rec = JSON.parse(stored) as AnalysisRecord;
        if (rec.type === 'ANALYSIS_TYPE_SECTOR') {
          handleLoadAnalysis(rec);
        }
      } catch {}
    }
  }, [handleLoadAnalysis]);

  // Fetch available tracks
  useEffect(() => {
    let cancelled = false;
    rpc<ListAnalysisTracksResponse>('ListAnalysisTracks', {}).then(resp => {
      if (!cancelled) {
        setAvailableTracks(resp.tracks || []);
      }
    }).catch(console.error);
    return () => { cancelled = true; };
  }, []);

  // Fetch sessions and track shape for selected track
  useEffect(() => {
    if (!selectedTrack) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const listResp = await rpc<ListSessionsResponse>('ListSessions', { pageSize: 100, track_name: selectedTrack });
        const filteredSessions = listResp.sessions || [];
        if (filteredSessions.length === 0) {
          setError(`${t('sector.noSessionsForTrack')} "${selectedTrack}"`);
          setLoading(false);
          return;
        }

        const detail = await rpc<SessionDetailResponse>('GetSession', { sessionId: filteredSessions[0].id });
        const tId = detail.session.trackId;
        const sessionIds = filteredSessions.map(s => s.id);

        const refResp = await rpc<AnalyzeSectorResponse>('AnalyzeSector', {
          trackId: tId,
          sessionIds,
        });

        if (cancelled) return;

        let shape: TrackShape | null = null;
        let annots: TrackAnnotation[] = [];
        try {
          const shapeResp = await rpc<{ shape: TrackShape | null; annotations: TrackAnnotation[] }>('GetTrackShapeAndAnnotations', { trackId: tId });
          if (shapeResp.shape && shapeResp.shape.centerline && shapeResp.shape.centerline.length >= 3) {
            shape = shapeResp.shape;
          }
          annots = shapeResp.annotations || [];
        } catch (e) {
          console.warn('Track shape not available, falling back to telemetry:', e);
        }

        setSessions(filteredSessions);
        setTrackId(tId);
        setRefSamples(refResp.referenceTrack || []);
        setTrackShape(shape);
        setTrackAnnotations(annots);

        const wizardUpdates: Partial<typeof wizardState> = {};

        // Prefill splits from sector annotations if no custom splits configured yet
        if (shape && annots.length > 0) {
          const finishLine = annots.find(a => a.type === 'finish_line');
          const sectorAnnots = annots.filter(a => a.type === 'sector');
          const samples = sampleClosedSpline(shape.centerline, shape.trackWidthM || 8.0, 30);
          const finishT = (finishLine?.location && samples.length > 0)
            ? findClosestParametricT(finishLine.location, samples).t
            : 0;

          if (splitPositions.length === 0 && sectorAnnots.length > 0) {
            const sortedSectors = [...sectorAnnots].sort((s1, s2) => {
              const loc1 = s1.location || { lat: 0, lng: 0 };
              const loc2 = s2.location || { lat: 0, lng: 0 };
              const t1 = findClosestParametricT(loc1, samples).t;
              const t2 = findClosestParametricT(loc2, samples).t;
              const rel1 = ((t1 - finishT) % 1.0 + 1.0) % 1.0;
              const rel2 = ((t2 - finishT) % 1.0 + 1.0) % 1.0;
              return rel1 - rel2;
            });

            const initialSplits: TrackPoint[] = sortedSectors
              .map(s => s.location)
              .filter((loc): loc is TrackPoint => !!loc);

            const initialPositions = initialSplits.map(sp => {
              const t = findClosestParametricT(sp, samples).t;
              return ((t - finishT) % 1.0 + 1.0) % 1.0;
            });

            setSplitPoints(initialSplits);
            wizardUpdates.splitPositions = initialPositions;
          } else if (splitPositions.length > 0 && splitPoints.length === 0) {
            const reconstructedSplits = splitPositions.map(pos => {
              const t = (finishT + pos) % 1.0;
              return getPointAtT(t, shape.centerline);
            });
            setSplitPoints(reconstructedSplits);
          }
        }

        if (selectedIdsArr.length === 0) {
          // Default: select the linked session if provided, otherwise most recent
          const targetSession = initialSessionId
            ? filteredSessions.find(s => s.id === initialSessionId)
            : null;

          const defaultSession = targetSession || filteredSessions.reduce((latest, s) => {
            const sdt = (s.date || '') + ' ' + (s.time || '');
            const ldt = (latest.date || '') + ' ' + (latest.time || '');
            return sdt > ldt ? s : latest;
          }, filteredSessions[0]);

          wizardUpdates.selectedIds = [defaultSession.id];
          wizardUpdates.driverFilter = defaultSession.driverName || 'all';
        }

        if (Object.keys(wizardUpdates).length > 0) {
          updateWizardState(wizardUpdates);
        }
      } catch (e: any) {
        if (!cancelled) {
          const nf = e instanceof RpcError && e.isNotFound;
          setNotFound(nf);
          setError(nf ? 'The requested resource could not be found.' : (e.message || t('sector.failedToLoad')));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedTrack]);

  const handleRemoveSplit = useCallback((idx: number) => {
    setSplitPositions(prev => {
      const next = [...prev];
      next.splice(idx, 1);
      return next;
    });
    setSplitPoints(prevPts => {
      const nextPts = [...prevPts];
      nextPts.splice(idx, 1);
      return nextPts;
    });
  }, [setSplitPositions, setSplitPoints]);

  const handleClearSplits = useCallback(() => {
    setSplitPositions([]);
    setSplitPoints([]);
  }, [setSplitPositions, setSplitPoints]);

  const handleAnalyze = useCallback(async (options?: { skipSave?: boolean }) => {
    if (selectedIds.size === 0 || !trackId) return;
    setAnalyzing(true);
    try {
      const finishLine = trackAnnotations.find(a => a.type === 'finish_line')?.location || trackShape?.centerline?.[0];
      const sectorsPayload: SectorsSelection = {
        splitPositions: [...splitPositions],
      };
      if (trackShape && trackShape.centerline && trackShape.centerline.length >= 3 && splitPositions.length > 0 && finishLine) {
        if (splitPoints.length === splitPositions.length) {
          sectorsPayload.splitPoints = splitPoints;
        } else {
          const samples = sampleClosedSpline(trackShape.centerline, trackShape.trackWidthM || 8.0, 30);
          const finishT = samples.length > 0 ? findClosestParametricT(finishLine, samples).t : 0;
          sectorsPayload.splitPoints = splitPositions.map(pos => getPointAtT((finishT + pos) % 1.0, trackShape.centerline));
        }
        sectorsPayload.finishLine = finishLine;
      }
      const resp = await rpc<AnalyzeResponse>('Analyze', {
        skipSave: options?.skipSave,
        sector: {
          track: { trackId, trackName: selectedTrack },
          sessions: { sessionIds: [...selectedIds] },
          sectors: sectorsPayload,
        },
      });
      if (resp.sector) {
        setAnalysisResult(resp.sector);
        setActiveSector(0);
        setStep(3);
      }
    } catch (e: any) {
      console.error(e);
    } finally {
      setAnalyzing(false);
    }
  }, [trackId, selectedTrack, selectedIds, splitPositions, splitPoints, trackShape, trackAnnotations, setActiveSector, setStep]);

  const handleStepClick = useCallback((targetStep: number) => {
    if (targetStep < step && selectedTrack) {
      setStep(targetStep);
    }
  }, [step, selectedTrack, setStep]);

  // Handle deep-linking into step 3 (e.g. on page refresh or direct URL navigation)
  useEffect(() => {
    if (step === 3 && !analysisResult && selectedIds.size > 0 && !analyzing && trackId) {
      handleAnalyze({ skipSave: true });
    }
  }, [step, analysisResult, selectedIds.size, analyzing, handleAnalyze, trackId]);

  const uniqueDrivers = [...new Set(sessions.map(s => s.driverName || '').filter(Boolean))].sort();

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorState message={error} isNotFound={notFound} />;

  const sectors = analysisResult?.sectors || [];

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <ToolHeader
        title={t('sector.title')}
        icon={BarChart2}
      />

      {/* Wizard stepper */}
      <div className="flex justify-center mb-6">
        <WizardStepper
          steps={wizardSteps}
          currentStep={step}
          onStepClick={handleStepClick}
        />
      </div>

      {/* Step 0: Choose Track */}
      {step === 0 && (
        <div className="max-w-2xl mx-auto">
          <div className="card overflow-hidden">
            <div className="px-4 py-3 border-b border-border-subtle">
              <h2 className="text-sm font-semibold text-text-primary">{t('sector.wizardChooseTrack')}</h2>
            </div>
            <div className="max-h-[400px] overflow-y-auto">
              {availableTracks.length > 0 ? (
                availableTracks.map(track => {
                  const isSelected = draftTrack === track;
                  return (
                    <div
                      key={track}
                      onClick={() => setDraftTrack(track)}
                      className={`flex items-center justify-between px-4 py-3 cursor-pointer transition-colors border-b border-border-subtle last:border-0 group ${
                        isSelected ? 'bg-accent/10 border-l-2 border-l-accent' : 'hover:bg-bg-hover'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          checked={isSelected}
                          onChange={() => setDraftTrack(track)}
                          className="w-3.5 h-3.5 accent-accent cursor-pointer"
                        />
                        <span className="text-sm font-medium text-text-primary">{track}</span>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="px-4 py-8 text-center text-text-muted text-sm">
                  {t('corner.noTracksWithCorners')}
                </div>
              )}
            </div>

            <div className="p-4 border-t border-border-subtle flex justify-end">
              <button
                disabled={!draftTrack}
                onClick={() => {
                  if (draftTrack) {
                    const isNewTrack = draftTrack !== selectedTrack;
                    setSelectedTrack(draftTrack);
                    navigate(`#/analysis/sector/${draftTrack}`, true);
                    if (isNewTrack) {
                      setSplitPoints([]);
                      setAnalysisResult(null);
                      updateWizardState({
                        step: 1,
                        splitPositions: [],
                        selectedIds: [],
                        activeSector: 0,
                      }, false);
                    } else {
                      setStep(1);
                    }
                  }
                }}
                className={`
                  px-4 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                  ${draftTrack ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
                `}
              >
                {t('common.next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Step 1: Define Sectors */}
      {step === 1 && (
        <div className="max-w-xl mx-auto flex flex-col items-center gap-3 w-full">
          {/* Header */}
          <div className="flex flex-col items-center text-center gap-1">
            <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">
              {t('sector.defineSectors')}
            </span>
            <span className="text-[0.75rem] text-text-muted">
              {t('sector.clickToAdd')}
            </span>
          </div>

          {/* Free-floating Track map */}
          <div className="w-full">
            <SectorTrackMap
              samples={refSamples}
              trackShape={trackShape}
              trackAnnotations={trackAnnotations}
              splitPositions={splitPositions}
              splitPoints={splitPoints}
              onSplitsChange={(splits, pts) => {
                setSplitPositions(splits);
                if (pts) setSplitPoints(pts);
              }}
            />
          </div>

          {/* Floating Split Points Bar */}
          <div className="w-full">
            <SplitPoints
              splitPositions={splitPositions}
              onRemove={handleRemoveSplit}
              onClearAll={handleClearSplits}
            />
          </div>

          <div className="flex items-center justify-between pt-2 w-full">
            <button
              onClick={() => setStep(0)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              onClick={() => setStep(2)}
              className="flex items-center gap-1.5 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer bg-accent text-white hover:bg-accent-hover"
            >
              <span>{t('sector.selectSessions')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 2: Select Sessions */}
      {step === 2 && (
        <div className="max-w-2xl mx-auto flex flex-col gap-4">
          <SessionSelector
            sessions={sessions}
            selectedIds={selectedIds}
            onSelectionChange={setSelectedIds}
            driverFilter={driverFilter}
            onDriverFilterChange={setDriverFilter}
            weatherFilter={weatherFilter}
            onWeatherFilterChange={setWeatherFilter}
            uniqueDrivers={uniqueDrivers}
          />

          <div className="flex items-center justify-between pt-2">
            <button
              onClick={() => setStep(1)}
              className="flex items-center gap-1.5 text-xs text-text-muted hover:text-text-primary transition-colors cursor-pointer"
            >
              <ArrowLeft size={14} />
              {t('common.back')}
            </button>

            <button
              disabled={selectedIds.size === 0 || analyzing}
              onClick={() => handleAnalyze()}
              className={`
                flex items-center gap-2 px-5 py-2 rounded-lg text-xs font-semibold transition-colors cursor-pointer
                ${selectedIds.size > 0 && !analyzing ? 'bg-accent text-white hover:bg-accent-hover' : 'bg-bg-surface text-text-muted cursor-not-allowed border border-border-subtle'}
              `}
            >
              {analyzing ? <LoadingSpinner size="sm" /> : <BarChart2 size={14} />}
              <span>{analyzing ? t('sector.analyzingLap') : t('sector.title')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Results */}
      {step === 3 && (
        <div className="flex flex-col gap-6">
          {analyzing ? (
            <LoadingSpinner />
          ) : sectors.length === 0 ? (
            <div className="card p-12 text-center text-text-muted text-sm">
              <span className="text-3xl opacity-60 mb-2 block">📊</span>
              <p className="text-sm text-text-secondary">{t('sector.noResults')}</p>
              <p className="text-xs text-text-muted mt-1">{t('sector.noResultsHint')}</p>
            </div>
          ) : (
            <>
              {/* Readonly track map */}
              <div className="w-full max-w-xl mx-auto flex flex-col items-center gap-3">
                <div className="text-center">
                  <span className="text-xs font-semibold uppercase tracking-widest text-text-secondary">{t('sector.trackSectors')}</span>
                </div>
                <div className="w-full">
                  <SectorTrackMap
                    samples={refSamples}
                    trackShape={trackShape}
                    trackAnnotations={trackAnnotations}
                    splitPositions={splitPositions}
                    splitPoints={splitPoints}
                    readonly
                    drawRibbonFill={false}
                  />
                </div>
                {/* Floating Sector Legend below map */}
                {sectors.length > 0 && (
                  <div className="flex items-center justify-center gap-2 p-2 rounded-2xl sm:rounded-full bg-bg-raised/80 backdrop-blur-md border border-border-subtle/80 shadow-lg flex-wrap">
                    {sectors.map((_, si) => {
                      const hue = (si * 360 / sectors.length) % 360;
                      return (
                        <div
                          key={si}
                          className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium mono"
                          style={{
                            background: `hsla(${hue}, 70%, 55%, 0.12)`,
                            border: `1px solid hsla(${hue}, 70%, 55%, 0.3)`,
                            color: `hsl(${hue}, 70%, 65%)`,
                          }}
                        >
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: `hsl(${hue}, 70%, 55%)` }}
                          />
                          <span>S{si + 1}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <TheoreticalBest
                result={analysisResult!}
                sectors={sectors}
              />

              {sectors.length > 1 && (
                <SectorTabs
                  sectors={sectors}
                  activeSector={activeSector}
                  onSectorChange={setActiveSector}
                />
              )}

              {sectors.map((sector, si) => (
                <div key={si} className={si === activeSector ? '' : 'hidden'}>
                  <SectorPanel
                    sector={sector}
                    sectorIndex={si}
                    totalSectors={sectors.length}
                    sessions={sessions}
                    activeMetric={activeMetric}
                    onMetricChange={setActiveMetric}
                  />
                </div>
              ))}

              <LapStatsTable
                sectors={sectors}
                sessions={sessions}
                lapSummaries={analysisResult?.lapSummaries}
                bestSectorTimesMs={analysisResult?.bestSectorTimesMs}
              />
            </>
          )}
        </div>
      )}

      {sessionRemovedAnalysis && (
        <SessionRemovedAnalysisDialog
          open={!!sessionRemovedAnalysis}
          onClose={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
          analysis={sessionRemovedAnalysis}
          onDeleted={() => {
            setSessionRemovedAnalysis(null);
            navigate('#/analysis/history');
          }}
        />
      )}
    </div>
  );
}
